import {
  computed,
  h,
  defineComponent,
  onUnmounted,
  ref,
  watch,
  provide,
  Ref,
} from 'vue';
import { t } from '@/ui/i18n/i18n';
import { container, loader } from '@/ui/components';
import { get, update } from '@/services/api/resources/inspection';
import { filterListToOptions, IdProps, pushRoute, sequence, toasted } from '@/ui/helpers/uiHelper';
import { filter as getAllUsers, get as getUser } from '@/services/api/resources/user';
import { hasFeature, hasPermission, Permissions, user } from '@/ui/auth/auth';
import { useTask } from '@/ui/concerns';
import ConductInspection from '@/_legacy/views/ConductInspection/ConductInspection.vue';
import { SelectOptions } from '@/ui/types';
import { eventBus } from '@/_legacy/eventBus';
import { Events } from '@/ui/events';
import { renderResourceTeaserPage } from '@/ui/pages/teasers/ResourceTeaserPage';
import { now } from '@/_legacy/util/UnixTimestamps';
import { LegacyChecklistRecord } from '@/ui/pages/inspection/LegacyChecklistRecord';
import { downloadApp } from '@/ui/components/onboarding/DownloadApp';
import { NamedLocation } from '@/ui/routes/types';
import { EventBus } from '@/ui/EventBus';
import { InspectionNavbar } from '@/ui/pages/inspection/InspectionNavbar';
import { UserDetail } from '@/entities/user';
import * as inspectionAssetsPhotos from '@/services/assets-api/inspectionAssetsPhotos';
import { INSPECTION_STATUS } from '@/_legacy/constants';
import {
  useEntityTypeOfResponseFirstQuestionMapping,
} from '@/ui/concerns/conductInspection/useEntityTypeOfResponseFirstQuestionMapping';
import { processFiles } from '@/services/assets-api/inspectionAssetsFiles';
import { useConductStore } from '@/stores/conduct/conduct';
import { isTranslationEnabled } from '@/ui/helpers/checklistHelper';

//to conduct inspection from inspection id
export const InspectionEditPage = defineComponent<IdProps>({
  name: 'InspectionEditPage',
  props: ['id'],
  setup(props) {
    if (!hasFeature('inspections')) {
      return renderResourceTeaserPage(t('common.inspections'));
    }

    const userOptions = ref<SelectOptions>([]);
    const record = ref<LegacyChecklistRecord>();
    const loggedInUserDetail = ref<UserDetail>();
    const isDraft = ref<boolean>(false);

    provide<Ref<SelectOptions>>('userOptions', userOptions);

    // get record
    const getRecord = useTask(
      () => sequence(get(props.id), getAllUsers(), getUser(user.value.id)),
      ([data, users, { data: userData }]) => {
        record.value = data.parsed as LegacyChecklistRecord;
        record.value.started_at = record.value.started_at || now();
        record.value.blurFaces = record.value.blurFaces ?? false;
        record.value.improveWriting = record.value.improveWriting ?? true;
        isDraft.value = record.value.status === INSPECTION_STATUS.DRAFT;
        userOptions.value = filterListToOptions(users);
        loggedInUserDetail.value = userData;
        conductStore.setIsTranslationEnabled(isTranslationEnabled(record.value));
      },
    );
    watch(ref(props.id), getRecord.run);

    // send record
    const sendRecord = useTask(
      () => update(props.id, record.value, user.value.timezone),
      response => {
        eventBus.$emit(Events.inspectionConducted, { isNew: false });

        const returnedId = response.id || props.id;

        const routeLocation: NamedLocation = hasPermission(Permissions.VIEW_OWN_INSPECTION)
          ? {
            name: 'inspectionDetail',
            params: {
              id: returnedId.toString(),
              showConfetti: isDraft.value ? 'true' : 'false',
            },
          }
          : { name: 'forms', params: { tab: 'draft' } };

        toasted.success(t('inspections.saved'));
        pushRoute(routeLocation);
      },
    );

    const sendDraftRecord = useTask(
      () => update(props.id, record.value, user.value.timezone),
      () => {
        eventBus.$emit(Events.inspectionConducted, { isNew: false });

        pushRoute({ name: 'forms', query: { tab: 'draft' } });
        toasted.success(t('inspections.draft-saved'));
      },
    );

    const conductStore = useConductStore();

    const photoAssetsLoading = ref(false);
    const fileAssetsLoading = ref(false);
    const submitIsLoading = computed(
      () => sendRecord.state.value.pending
        || sendDraftRecord.state.value.pending
        || photoAssetsLoading.value
        || fileAssetsLoading.value,
    );

    const { mapEntityTypeOfResponseFirstQuestion } = useEntityTypeOfResponseFirstQuestionMapping();

    const onSubmit = async ({ inspection }: { inspection: LegacyChecklistRecord }) => {
      mapEntityTypeOfResponseFirstQuestion(inspection);

      record.value = inspection;
      record.value!.conducted_at = now();
      record.value!.status = 10;

      photoAssetsLoading.value = true;
      await inspectionAssetsPhotos.processPhotos(record.value);
      photoAssetsLoading.value = false;

      fileAssetsLoading.value = true;
      await processFiles(record.value);
      fileAssetsLoading.value = false;

      sendRecord.run();
    };

    const onSubmitDraft = async ({ inspection }: { inspection: LegacyChecklistRecord }) => {
      mapEntityTypeOfResponseFirstQuestion(inspection);

      record.value = inspection;
      record.value!.status = INSPECTION_STATUS.DRAFT;
      record.value!.last_updated = now();

      photoAssetsLoading.value = true;
      await inspectionAssetsPhotos.processPhotos(record.value);
      photoAssetsLoading.value = false;

      fileAssetsLoading.value = true;
      await processFiles(record.value);
      fileAssetsLoading.value = false;

      sendDraftRecord.run();
    };

    const detachEventListeners = () => {
      EventBus.$off(Events.submittedInspection, onSubmit);
      EventBus.$off(Events.submittedDraftInspection, onSubmitDraft);
      conductStore.$reset();
    };

    EventBus.$on(Events.submittedInspection, onSubmit);
    EventBus.$on(Events.submittedDraftInspection, onSubmitDraft);

    onUnmounted(detachEventListeners);

    return function render() {
      return loader('div', getRecord, [
        record.value ? h(InspectionNavbar, {
          props: {
            record: record.value,
            loading: submitIsLoading.value,
            saving: sendRecord.state.value.pending,
            submitting: sendDraftRecord.state.value.pending,
          },
        }) : null,
        container([
          downloadApp(),
          record.value ? h(ConductInspection, {
            class: 'legacy',
            props: {
              resource: record.value,
              assignees: userOptions.value,
              loading: submitIsLoading.value,
              userDetail: loggedInUserDetail.value?.data,
            },
          }) : null,
        ]),
      ], false, 'create-inspection-page');
    };
  },
});
